import React, { useEffect, useState } from 'react';
import { API } from '../api';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { Field, RadioRows, Segmented, Select, controlClass } from '../ui/Field';
import { useToast } from '../ui/Toast';
import { TEAM_OPTIONS } from './ticketFields';

const CVE_PATTERN = /^CVE-\d{4}-\d{4,}$/i;
const TEAM_FLOWS = TEAM_OPTIONS.map((t) => t.value);

const TYPE_OPTIONS = [
  { value: 'host-based', label: 'Hosts' },
  { value: 'package-based', label: 'Package' },
  { value: 'cve-based', label: 'CVE' },
];
const TYPE_HINTS = {
  'host-based': 'One ticket per host with matching vulnerabilities.',
  'package-based': 'Tickets for the hosts that run a vulnerable version of the package.',
  'cve-based': 'Tickets for the hosts affected by the CVE.',
};
const TEAM_CHOICES = [{ value: 'all', label: 'All teams' }, ...TEAM_OPTIONS];
const SEVERITY_CHOICES = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];
const MODE_CHOICES = [
  { value: 'preview', label: 'Preview', description: 'Saved here only. Review them, then open them in Jotform.' },
  { value: 'normal', label: 'Normal', description: 'Opens the tickets in Jotform right away.' },
];

const INITIAL_FORM = {
  type: 'host-based',
  team: 'dba',
  mode: 'preview',
  severity: 'all',
  fromDate: '',
  maxTickets: 2,
  maxLines: 500,
  cve: '',
  packageName: '',
};

// YYYY-MM-DD from the date input → DD_MM_YYYY, the format the ticket flows expect.
const toFlowDate = (value) => {
  if (!value) return undefined;
  const parts = value.split('-');
  return parts.length === 3 ? `${parts[2]}_${parts[1]}_${parts[0]}` : value;
};

function validate(form) {
  if (form.type === 'cve-based') {
    const cve = form.cve.trim();
    if (!cve) return { cve: 'Enter a CVE ID.' };
    if (!CVE_PATTERN.test(cve)) return { cve: 'Use the CVE-YYYY-NNNN format, for example CVE-2024-5535.' };
  }
  if (form.type === 'package-based' && !form.packageName.trim()) return { packageName: 'Enter a package name.' };
  return {};
}

// The same requests the legacy Tickets page sent, one per selected team.
async function createTickets(form) {
  const isNormal = form.mode === 'normal';
  const options = {
    preview_mode: form.mode === 'preview',
    ticket_limit: form.type === 'host-based' && isNormal && Number.isFinite(form.maxTickets) ? form.maxTickets : undefined,
    max_ticket_size: isNormal && Number.isFinite(form.maxLines) ? form.maxLines : undefined,
  };
  const severity = form.severity !== 'all' ? form.severity : undefined;

  if (form.type === 'cve-based') {
    options.cve = form.cve.trim().toUpperCase();
    options.max_ticket_number = undefined;
    options.max_ticket_line = isNormal && Number.isFinite(form.maxLines) ? form.maxLines : undefined;
  } else if (form.type === 'package-based') {
    options.package = form.packageName.trim();
    options.severity = severity;
    options.from_date = toFlowDate(form.fromDate);
  } else {
    options.severity = severity;
    options.from_date = toFlowDate(form.fromDate);
  }

  const flows = form.team === 'all' ? TEAM_FLOWS : [form.team];
  const request = { 'cve-based': API.CREATE_CVE_TICKETS, 'package-based': API.CREATE_PACKAGE_TICKETS }[form.type] || API.CREATE_TICKETS;
  // One at a time: the API runs a single ticket flow at a time and answers 409 to the rest.
  const responses = [];
  for (const flow of flows) {
    responses.push(await request({ ...options, flow_type: flow }));
  }
  return { flows, responses };
}

function summarize({ flows, responses }) {
  const messages = responses
    .map((response, i) => {
      const count = response?.tickets_created;
      const message = response?.message ?? (typeof count !== 'number' ? null : count === 0 ? 'No tickets were created: no unticketed findings matched. Findings already in open tickets are skipped.' : `Created ${count} ticket${count === 1 ? '' : 's'}.`);
      if (!message) return null;
      return flows.length === 1 ? message : `${TEAM_OPTIONS.find((t) => t.value === flows[i]).label}: ${message}`;
    })
    .filter(Boolean);
  const created = responses.reduce((sum, r) => sum + (r?.tickets_created || 0), 0);
  const skippedHosts = [...new Set(responses.flatMap((r) => (Array.isArray(r?.skipped_hosts) ? r.skipped_hosts : [])))].sort();
  return { message: messages.join(' '), created, skippedHosts };
}

const numberValue = (event) => (event.target.value === '' ? NaN : Number(event.target.value));

/**
 * Side panel that creates tickets through the ticket flows.
 * lock: { type: 'cve-based', cve } | { type: 'package-based', packageName } fixes the entity (CVE and package pages).
 * onCreated({ created, skippedHosts }) runs after the requests succeed; the panel shows the toast itself.
 */
export default function CreateTicketsPanel({ open, onClose, onCreated, lock }) {
  const toast = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [busy, setBusy] = useState(false);
  const values = lock ? { ...form, ...lock } : form;
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    setSubmitError(null);
    if (Object.keys(found).length > 0) {
      document.getElementById(`create-tickets-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    setBusy(true);
    try {
      const summary = summarize(await createTickets(values));
      if (!summary.message) toast.error('Ticket creation finished, but the server sent no result message.');
      else if (summary.created > 0) toast.success(summary.message);
      else toast.info(summary.message);
      onCreated?.(summary);
      onClose();
    } catch (error) {
      setSubmitError(error.message || 'The request did not complete.');
    } finally {
      setBusy(false);
    }
  };

  const isCve = values.type === 'cve-based';
  const isNormal = values.mode === 'normal';
  const lockedEntity = lock && (isCve ? values.cve : values.packageName);

  return (
    <SidePanel
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Create tickets"
      description={lock ? (isCve ? 'For the hosts affected by this CVE.' : 'For the hosts that run a vulnerable version of this package.') : null}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="create-tickets-form" variant="primary" disabled={busy}>
            {busy ? 'Creating tickets…' : 'Create tickets'}
          </Button>
        </>
      )}
    >
      <form id="create-tickets-form" onSubmit={submit} noValidate className="space-y-5">
        {lock ? (
          <div>
            <p className="mb-1 text-small font-semibold text-ink">{isCve ? 'CVE' : 'Package'}</p>
            <p className="font-mono text-id font-semibold text-ink break-all">{lockedEntity}</p>
            {!isCve && <p className="mt-1 text-small text-graphite">Covers every installed version of {lockedEntity}.</p>}
          </div>
        ) : (
          <div>
            <p className="mb-1 text-small font-semibold text-ink" aria-hidden="true">Based on</p>
            <Segmented label="Based on" options={TYPE_OPTIONS} value={values.type} onChange={set('type')} disabled={busy} />
            <p className="mt-1 text-small text-graphite">{TYPE_HINTS[values.type]}</p>
          </div>
        )}

        {!lock && values.type === 'cve-based' && (
          <Field id="create-tickets-cve" label="CVE" error={errors.cve}>
            <input
              id="create-tickets-cve"
              type="text"
              value={values.cve}
              onChange={(e) => set('cve')(e.target.value)}
              placeholder="CVE-2024-5535"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-invalid={Boolean(errors.cve)}
              aria-describedby={errors.cve ? 'create-tickets-cve-error' : undefined}
              className={controlClass({ invalid: Boolean(errors.cve), mono: true })}
            />
          </Field>
        )}
        {!lock && values.type === 'package-based' && (
          <Field id="create-tickets-packageName" label="Package name" hint="The exact package name. Every installed version is included." error={errors.packageName}>
            <input
              id="create-tickets-packageName"
              type="text"
              value={values.packageName}
              onChange={(e) => set('packageName')(e.target.value)}
              placeholder="openssl"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-invalid={Boolean(errors.packageName)}
              aria-describedby={errors.packageName ? 'create-tickets-packageName-error' : 'create-tickets-packageName-hint'}
              className={controlClass({ invalid: Boolean(errors.packageName), mono: true })}
            />
          </Field>
        )}

        <Field id="create-tickets-team" label="Team" hint={values.team === 'all' ? 'Runs once for each team.' : null}>
          <Select id="create-tickets-team" options={TEAM_CHOICES} value={values.team} onChange={(e) => set('team')(e.target.value)} disabled={busy} />
        </Field>

        {!isCve && (
          <div className="grid grid-cols-2 gap-3">
            <Field id="create-tickets-severity" label="Severity">
              <Select id="create-tickets-severity" options={SEVERITY_CHOICES} value={values.severity} onChange={(e) => set('severity')(e.target.value)} disabled={busy} />
            </Field>
            <Field id="create-tickets-from" label="Found since" hint="Empty includes all.">
              <input
                id="create-tickets-from"
                type="date"
                value={values.fromDate}
                onChange={(e) => set('fromDate')(e.target.value)}
                disabled={busy}
                className={`${controlClass()} tabular-nums`}
              />
            </Field>
          </div>
        )}

        <RadioRows name="create-tickets-mode" label="Mode" options={MODE_CHOICES} value={values.mode} onChange={set('mode')} disabled={busy} />

        {isNormal && (
          <div className="grid grid-cols-2 gap-3">
            {values.type === 'host-based' && (
              <Field id="create-tickets-max" label="Max tickets">
                <input id="create-tickets-max" type="number" min="1" value={Number.isFinite(values.maxTickets) ? values.maxTickets : ''} onChange={(e) => set('maxTickets')(numberValue(e))} disabled={busy} className={`${controlClass()} tabular-nums`} />
              </Field>
            )}
            <Field id="create-tickets-lines" label="Max lines per ticket">
              <input id="create-tickets-lines" type="number" min="1" value={Number.isFinite(values.maxLines) ? values.maxLines : ''} onChange={(e) => set('maxLines')(numberValue(e))} disabled={busy} className={`${controlClass()} tabular-nums`} />
            </Field>
          </div>
        )}

        {submitError && (
          <div role="alert" className="px-3 py-2.5 rounded-control border border-crit/40 bg-crit-tint">
            <p className="text-body font-semibold text-crit">Tickets could not be created</p>
            <p className="mt-1 font-mono text-small text-ink break-words">{submitError}</p>
          </div>
        )}
      </form>
    </SidePanel>
  );
}
